<script lang="ts">
	import Icon from './Icon.svelte';
	
	interface Props {
		code: string;
		language?: 'bash' | 'shell' | 'json' | 'javascript';
		showCopy?: boolean;
		title?: string;
	}
	
	let { code, language = 'bash', showCopy = true, title = 'Terminal' }: Props = $props();
	
	let copied = $state(false);
	
	const copyToClipboard = async () => {
		try {
			// Extract only executable commands (remove comments and empty lines)
			const executableLines = code
				.split('\n')
				.map(line => line.trim()) // Trim first to normalize
				.filter(line => {
					// Skip empty lines and comments
					if (!line || line.startsWith('#')) return false;
					return true;
				})
				.map(line => {
					// Remove $ prompt if present
					return line.replace(/^\$\s*/, '');
				})
				.filter(line => line.length > 0);
			
			const cleanCode = executableLines.join('\n');
			await navigator.clipboard.writeText(cleanCode);
			copied = true;
			setTimeout(() => copied = false, 2000);
		} catch (err) {
			console.error('Failed to copy:', err);
		}
	};
	
	// Process code for display with syntax highlighting
	const processCode = (text: string) => {
		const lines = text.split('\n');
		const processedLines: string[] = [];
		
		const commands = [
			'brew', 'scoop', 'apt', 'apt-get', 'dpkg', 'dnf', 'yum', 'pacman', 'snap',
			'npm', 'yarn', 'pnpm', 'npx', 'node', 'deno', 'bun',
			'git', 'gh', 'hub',
			'curl', 'wget', 'tar', 'unzip', 'gunzip',
			'sudo', 'chmod', 'chown', 'mkdir', 'rm', 'rmdir', 'cp', 'mv', 'ln',
			'cd', 'ls', 'pwd', 'echo', 'cat', 'grep', 'sed', 'awk', 'find',
			'hw', 'heyweek',
			'docker', 'kubectl', 'helm',
			'python', 'pip', 'ruby', 'gem', 'go', 'cargo', 'rustc',
			'bash', 'sh', 'zsh'
		];
		
		for (const line of lines) {
			const trimmed = line.trim();
			
			// Handle empty lines
			if (!trimmed) {
				processedLines.push('');
				continue;
			}
			
			// Handle comments - preserve original indentation for comments
			if (trimmed.startsWith('#')) {
				if (trimmed === '# OR') {
					processedLines.push('<span class="terminal__comment"># OR</span>');
				} else {
					// For comments, just use the trimmed version to avoid indentation issues
					processedLines.push(`<span class="terminal__comment">${escapeHtml(trimmed)}</span>`);
				}
				continue;
			}
			
			// Check if line already has a prompt
			let lineContent = trimmed;
			let hasPrompt = false;
			
			if (trimmed.startsWith('$')) {
				hasPrompt = true;
				lineContent = trimmed.substring(1).trim();
			}
			
			// Check if line starts with a command
			const words = lineContent.split(/\s+/);
			const firstWord = words[0].toLowerCase();
			
			// Check if it's a known command or looks like a command
			const isCommand = commands.includes(firstWord) || 
			                  firstWord === 'hw' || 
			                  firstWord === 'heyweek' ||
			                  // Also match commands with flags like "hw --version" or "hw version"
			                  (firstWord === 'hw' && words.length > 0);
			
			// Process the line with syntax highlighting
			if (isCommand || hasPrompt) {
				const highlightedLine = highlightSyntax(lineContent);
				processedLines.push(`<span class="terminal__prompt">$</span> ${highlightedLine}`);
			} else {
				// Output line (not a command)
				processedLines.push(`<span class="terminal__output">${escapeHtml(trimmed)}</span>`);
			}
		}
		
		return processedLines.join('\n');
	};
	
	// Escape HTML special characters
	const escapeHtml = (text: string): string => {
		const map: { [key: string]: string } = {
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#039;'
		};
		return text.replace(/[&<>"']/g, (m) => map[m]);
	};
	
	// Apply syntax highlighting to a command line
	const highlightSyntax = (line: string): string => {
		const commands = [
			'brew', 'scoop', 'apt', 'apt-get', 'dpkg', 'dnf', 'yum', 'pacman', 'snap',
			'npm', 'yarn', 'pnpm', 'npx', 'node', 'deno', 'bun',
			'git', 'gh', 'hub',
			'curl', 'wget', 'tar', 'unzip', 'gunzip',
			'sudo', 'chmod', 'chown', 'mkdir', 'rm', 'rmdir', 'cp', 'mv', 'ln',
			'cd', 'ls', 'pwd', 'echo', 'cat', 'grep', 'sed', 'awk', 'find',
			'hw', 'heyweek',
			'docker', 'kubectl', 'helm',
			'python', 'pip', 'ruby', 'gem', 'go', 'cargo', 'rustc',
			'bash', 'sh', 'zsh',
			'install', 'tap', 'bucket', 'add', 'version', 'auth', 'login', 'logout', 'renew',
			'timer', 'start', 'stop', 'status', 'project', 'create', 'delete', 'list',
			'log'
		];
		
		const subCommands = [
			'install', 'tap', 'bucket', 'add', 'version', 'auth', 'login', 'logout', 'renew',
			'timer', 'start', 'stop', 'status', 'project', 'create', 'delete', 'list',
			'log', 'push', 'pull', 'commit', 'checkout', 'branch', 'merge', 'rebase',
			'init', 'clone', 'fetch', 'remote', 'config'
		];
		
		// Split the line into tokens while preserving quotes
		const tokens: string[] = [];
		let current = '';
		let inQuote = false;
		let quoteChar = '';
		
		for (let i = 0; i < line.length; i++) {
			const char = line[i];
			
			if ((char === '"' || char === "'") && (i === 0 || line[i - 1] !== '\\')) {
				if (!inQuote) {
					if (current) {
						tokens.push(current);
						current = '';
					}
					inQuote = true;
					quoteChar = char;
					current = char;
				} else if (char === quoteChar) {
					current += char;
					tokens.push(current);
					current = '';
					inQuote = false;
					quoteChar = '';
				} else {
					current += char;
				}
			} else if (char === ' ' && !inQuote) {
				if (current) {
					tokens.push(current);
					current = '';
				}
				tokens.push(' ');
			} else {
				current += char;
			}
		}
		
		if (current) {
			tokens.push(current);
		}
		
		// Apply highlighting to each token
		const highlightedTokens = tokens.map((token, index) => {
			// Skip spaces
			if (token === ' ') return ' ';
			
			// Check if it's a quoted string
			if (token.startsWith('"') && token.endsWith('"') ||
			    token.startsWith("'") && token.endsWith("'")) {
				return `<span class="terminal__string">${escapeHtml(token)}</span>`;
			}
			
			// Check if it's a flag (starts with - or --)
			if (token.startsWith('--')) {
				// Long flag
				const parts = token.split('=');
				if (parts.length === 2) {
					return `<span class="terminal__flag">${escapeHtml(parts[0])}</span>=<span class="terminal__string">${escapeHtml(parts[1])}</span>`;
				}
				return `<span class="terminal__flag">${escapeHtml(token)}</span>`;
			} else if (token.startsWith('-') && token.length > 1 && !token.match(/^-\d/)) {
				// Short flag (but not negative numbers)
				return `<span class="terminal__flag">${escapeHtml(token)}</span>`;
			}
			
			// Check if it's a pipe or redirect operator
			if (token === '|' || token === '||' || token === '&&' || 
			    token === '>' || token === '>>' || token === '<' || token === '2>') {
				return `<span class="terminal__operator">${escapeHtml(token)}</span>`;
			}
			
			// Check if it's the main command (first non-space token)
			const nonSpaceTokens = tokens.filter(t => t !== ' ');
			const tokenIndex = nonSpaceTokens.indexOf(token);
			
			if (tokenIndex === 0) {
				// First token - check if it's a known command
				if (commands.includes(token.toLowerCase())) {
					return `<span class="terminal__command">${escapeHtml(token)}</span>`;
				}
			} else if (tokenIndex === 1) {
				// Second token - check if it's a subcommand
				if (subCommands.includes(token.toLowerCase())) {
					return `<span class="terminal__subcommand">${escapeHtml(token)}</span>`;
				}
			}
			
			// Check for paths (contains /)
			if (token.includes('/') || token.startsWith('~') || token.startsWith('.')) {
				return `<span class="terminal__path">${escapeHtml(token)}</span>`;
			}
			
			// Check for URLs
			if (token.startsWith('http://') || token.startsWith('https://') || token.startsWith('git@')) {
				return `<span class="terminal__url">${escapeHtml(token)}</span>`;
			}
			
			// Default: return as-is
			return escapeHtml(token);
		});
		
		return highlightedTokens.join('');
	};
	
	const displayCode = $derived(processCode(code));
</script>

<div class="terminal">
	<div class="terminal__header">
		<div class="terminal__dots">
			<span class="terminal__dot terminal__dot--close" aria-hidden="true"></span>
			<span class="terminal__dot terminal__dot--minimize" aria-hidden="true"></span>
			<span class="terminal__dot terminal__dot--maximize" aria-hidden="true"></span>
		</div>
		<span class="terminal__title">{title}</span>
		{#if showCopy}
			<button 
				class="terminal__copy"
				class:terminal__copy--copied={copied}
				onclick={copyToClipboard}
				aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
				title={copied ? 'Copied!' : 'Copy'}
			>
				{#if copied}
					<Icon name="check" size={14} />
				{:else}
					<Icon name="copy" size={14} />
				{/if}
			</button>
		{/if}
	</div>
	<div class="terminal__body">
		<pre class="terminal__content">{@html displayCode}</pre>
	</div>
</div>

<style>
	/* Default dark theme */
	.terminal {
		background: #0d1117;
		border-radius: 12px;
		box-shadow: 
			0 10px 40px rgba(0, 0, 0, 0.2),
			0 2px 10px rgba(0, 0, 0, 0.1);
		overflow: hidden;
		border: 1px solid rgba(240, 246, 252, 0.1);
		font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
	}

	/* Light theme overrides using data-theme attribute */
	:global([data-theme='light']) .terminal {
		background: #f6f8fa !important;
		border-color: rgba(27, 31, 36, 0.15) !important;
		box-shadow: 
			0 10px 40px rgba(0, 0, 0, 0.08),
			0 2px 10px rgba(0, 0, 0, 0.06) !important;
	}

	/* Dark theme explicit */
	:global([data-theme='dark']) .terminal {
		background: #0d1117 !important;
		border-color: rgba(240, 246, 252, 0.1) !important;
	}

	/* Default dark header */
	.terminal__header {
		background: linear-gradient(to bottom, #21262d, #161b22);
		padding: 0.625rem 1rem;
		display: flex;
		align-items: center;
		gap: 0.75rem;
		position: relative;
	}

	/* Light theme header */
	:global([data-theme='light']) .terminal__header {
		background: linear-gradient(to bottom, #ffffff, #f6f8fa) !important;
	}

	/* Dark theme header */
	:global([data-theme='dark']) .terminal__header {
		background: linear-gradient(to bottom, #21262d, #161b22) !important;
	}

	.terminal__dots {
		display: flex;
		gap: 0.5rem;
	}

	.terminal__dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
		transition: all 0.2s ease;
	}

	.terminal__dot--close {
		background: #ff5f57;
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
	}

	.terminal__dot--minimize {
		background: #ffbd2e;
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
	}

	.terminal__dot--maximize {
		background: #28ca42;
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
	}

	.terminal:hover .terminal__dot--close {
		background: #ff7870;
	}

	.terminal:hover .terminal__dot--minimize {
		background: #ffce42;
	}

	.terminal:hover .terminal__dot--maximize {
		background: #3cda55;
	}

	.terminal__title {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		font-size: 0.75rem;
		color: #8b949e;
		font-weight: 500;
		letter-spacing: 0.025em;
		pointer-events: none;
	}

	:global([data-theme='light']) .terminal__title {
		color: #57606a !important;
	}

	:global([data-theme='dark']) .terminal__title {
		color: #8b949e !important;
	}

	.terminal__copy {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		padding: 0;
		background: rgba(110, 118, 129, 0.1);
		border: 1px solid rgba(240, 246, 252, 0.1);
		border-radius: 6px;
		color: #8b949e;
		cursor: pointer;
		transition: all 0.2s ease;
		margin-left: auto;
	}

	:global([data-theme='light']) .terminal__copy {
		background: rgba(27, 31, 36, 0.06) !important;
		border-color: rgba(27, 31, 36, 0.15) !important;
		color: #57606a !important;
	}

	.terminal__copy:hover {
		background: rgba(110, 118, 129, 0.2);
		color: #c9d1d9;
		border-color: rgba(240, 246, 252, 0.2);
		transform: scale(1.05);
	}

	:global([data-theme='light']) .terminal__copy:hover {
		background: rgba(27, 31, 36, 0.12) !important;
		color: #24292f !important;
	}

	.terminal__copy--copied {
		background: rgba(46, 160, 67, 0.15);
		color: #3fb950;
		border-color: rgba(46, 160, 67, 0.3);
		animation: copySuccess 0.3s ease;
	}
	
	@keyframes copySuccess {
		0% { transform: scale(1); }
		50% { transform: scale(1.15); }
		100% { transform: scale(1); }
	}

	.terminal__body {
		padding: 0;
		background: #0d1117;
		overflow-x: auto;
	}

	:global([data-theme='light']) .terminal__body {
		background: #f6f8fa !important;
	}

	:global([data-theme='dark']) .terminal__body {
		background: #0d1117 !important;
	}

	.terminal__content {
		margin: 0;
		padding: 1rem;
		font-size: 0.875rem;
		line-height: 1.6;
		color: #c9d1d9;
		white-space: pre;
		letter-spacing: 0.025em;
		font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
	}

	:global([data-theme='light']) .terminal__content {
		color: #24292f !important;
	}

	:global([data-theme='dark']) .terminal__content {
		color: #c9d1d9 !important;
	}

	/* Default syntax highlighting (dark) */
	.terminal__content :global(.terminal__prompt) {
		color: #7ee83f;
		font-weight: 600;
		margin-right: 0.5ch;
	}

	.terminal__content :global(.terminal__command) {
		color: #58a6ff;
		font-weight: 500;
	}

	.terminal__content :global(.terminal__subcommand) {
		color: #79c0ff;
	}

	.terminal__content :global(.terminal__flag) {
		color: #ff7b72;
	}

	.terminal__content :global(.terminal__string) {
		color: #a5d6ff;
	}

	.terminal__content :global(.terminal__path) {
		color: #ffa657;
	}

	.terminal__content :global(.terminal__url) {
		color: #a5d6ff;
		text-decoration: underline;
		text-decoration-color: rgba(165, 214, 255, 0.3);
	}

	.terminal__content :global(.terminal__comment) {
		color: #8b949e;
		font-style: italic;
	}

	.terminal__content :global(.terminal__operator) {
		color: #ff7b72;
		font-weight: 600;
	}

	.terminal__content :global(.terminal__output) {
		color: #8b949e;
		opacity: 0.9;
	}

	/* Light theme syntax highlighting */
	:global([data-theme='light']) .terminal__content :global(.terminal__prompt) {
		color: #1a7f37 !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__command) {
		color: #0969da !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__subcommand) {
		color: #0969da !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__flag) {
		color: #cf222e !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__string) {
		color: #0a3069 !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__path) {
		color: #bc4c00 !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__url) {
		color: #0a3069 !important;
		text-decoration-color: rgba(10, 48, 105, 0.3) !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__comment) {
		color: #57606a !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__operator) {
		color: #cf222e !important;
	}

	:global([data-theme='light']) .terminal__content :global(.terminal__output) {
		color: #57606a !important;
	}

	/* Scrollbar styling */
	.terminal__body::-webkit-scrollbar {
		height: 8px;
		background: rgba(110, 118, 129, 0.1);
	}

	.terminal__body::-webkit-scrollbar-thumb {
		background: rgba(110, 118, 129, 0.3);
		border-radius: 4px;
	}

	.terminal__body::-webkit-scrollbar-thumb:hover {
		background: rgba(110, 118, 129, 0.5);
	}


	/* Responsive */
	@media (max-width: 640px) {
		.terminal__title {
			display: none;
		}

		.terminal__header {
			padding: 0.5rem 0.75rem;
		}

		.terminal__dots {
			gap: 0.375rem;
		}

		.terminal__dot {
			width: 10px;
			height: 10px;
		}

		.terminal__copy {
			width: 24px;
			height: 24px;
		}

		.terminal__body {
			padding: 0;
		}

		.terminal__content {
			padding: 0.75rem;
			font-size: 0.8125rem;
			line-height: 1.5;
		}
	}
</style>